/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (edit
 * mode — the page-with-back-button + FormGroup field layout, and the parent-picker idiom the
 * "Move" form below reuses).
 *
 * Only the data, columns and labels are ours. Reuses the same sources brand's own Detail.tsx cites
 * (screen-map.md row 12) — reparenting is not a `ReasonDialog` candidate here: that component is
 * for irreversible/destructive actions with a mandatory reason, and moving a category in the tree
 * is neither (it can be moved back).
 */
import { useState } from "react";
import { useLoaderData, useNavigate, useOutletContext, useRevalidator, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiArrowsMergeDuotone as MoveIcon,
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiLockDuotone as Lock,
  PiToggleRightDuotone as StatusIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { PageHeader, ErrorSurface, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { StatusSwitch } from "../../../components/status-switch";
import {
  deactivateCategory,
  byKey,
  getCategory,
  getCategoryBySlug,
  activeOrKept,
  listCategoryTree,
  reactivateCategory,
  reparentCategory,
  updateCategory,
  type Category,
} from "../../../lib/services/catalog";
import { imageUrl, useFileUpload, type UploadedFile } from "@steelyard/common";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formCardPadded, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { ActionIcon, Text, Button } from "rizzui";
import { RecordSummary } from "../../../components/detail-parts";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";
import { changedSlug, SLUG_HINT, slugSchema } from "../../../lib/slug";
import { categoryPath } from "../../../lib/paths";

/**
 * Both calls this screen needs: the category itself, and the tree for the "Move" picker — active
 * categories only, plus its current parent if since deactivated (`activeOrKept`).
 */
export async function categoryDetailLoader({ params, request }: LoaderFunctionArgs) {
  const [category, tree] = await Promise.all([byKey(request, params.slug!, getCategory, getCategoryBySlug, categoryPath), listCategoryTree()]);
  if (!category || !tree) throw new Error("The API returned no body for this category.");
  return { category, tree: activeOrKept(tree, [category.parentId]) };
}

const categoryEditSchema = z.object({
  name: z.string().trim().min(1, "Required").max(200),
  slug: slugSchema,
  sortOrder: z.string().trim().min(1, "Required").regex(/^\d+$/, "Enter a whole number"),
  isActive: z.boolean(),
});
type CategoryEditValues = z.infer<typeof categoryEditSchema>;

export default function CategoryDetail() {
  const { category, tree } = useLoaderData() as Awaited<ReturnType<typeof categoryDetailLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const revalidator = useRevalidator();
  // Same as brands/Detail.tsx: Back is a history step, and the bare list path is the Active view.
  const goBack = useGoBack("/catalogue/categories");
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [moveError, setMoveError] = useState<unknown>(null);
  const [imageError, setImageError] = useState<unknown>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  // Seeded from the persisted image so a reload shows the existing picture, same idiom as
  // products/Detail.tsx's `imageHash`/`uploadedImage` split.
  const [imageHash, setImageHash] = useState<string | null>(category.imageHash);
  const [uploadedImage, setUploadedImage] = useState<UploadedFile | null>(null);
  const image = useFileUpload("image", {
    onUploaded: async (result) => {
      setImageError(null);
      setUploadedImage(result);
      setImageHash(result.hash);
      await updateCategory(category.id, { name: category.name, sortOrder: category.sortOrder, imageHash: result.hash });
      toast.success("Image uploaded");
    },
    onError: () => toast.error("Could not upload the image"),
  });
  // `isUploadingImage`/`imageError` now cover only the remove; the upload's own are `image.busy`/`image.error`.
  const imageBusy = image.busy || isUploadingImage;

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this category" />;
  }
  const canWrite = can(permissionState, "catalogue.administer");

  async function handleRemoveImage() {
    setImageError(null);
    setIsUploadingImage(true);
    try {
      await updateCategory(category.id, { name: category.name, sortOrder: category.sortOrder, imageHash: null });
      setImageHash(null);
      setUploadedImage(null);
      toast.success("Image removed");
    } catch (err) {
      setImageError(err);
      toast.error("Could not remove the image");
    } finally {
      setIsUploadingImage(false);
    }
  }

  async function handleMove(parentId: string | null) {
    setMoveError(null);
    try {
      await reparentCategory(category.id, parentId);
      toast.success("Category moved");
      // Re-run this route's loader in place so the badge/parent picker reflect the new state
      // without losing scroll position — same idiom as customers/Detail.tsx and Dispatch.tsx.
      revalidator.revalidate();
    } catch (err) {
      // A 422 here is the backend's own cycle guard (Category.Reparent) — surfaced verbatim, not
      // pre-validated by walking descendants client-side (constitution I's spirit).
      setMoveError(err);
      toast.error("Could not move the category");
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={category.name}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Categories", href: "/catalogue/categories" }, { name: category.name }]}
      />
      <RecordSummary code={category.code} />

      {canWrite ? (
        // Its own form: a second <form> cannot sit inside the details form. Above it, not below, so
        // the details form is the page's last element and its Save bar sits at the screen's bottom.
        <div className={formCardPadded}>
          <FormGroup title={<SectionTitle icon={MoveIcon}>Move</SectionTitle>} description="Change this category's parent. The API refuses a move that would create a cycle.">
            {moveError ? <ErrorSurface error={moveError} action="move this category" /> : null}

            <Form<{ parentId: string }>
              useFormProps={{ defaultValues: { parentId: category.parentId ?? "" } }}
              className="col-span-full flex flex-col gap-3 @sm:flex-row @sm:items-end @5xl:col-span-2"
              onSubmit={async (values) => handleMove(values.parentId || null)}
            >
              {({ register, formState: { isSubmitting } }) => (
                <>
                  <div className="flex-1">
                    <SelectField label="New parent" {...register("parentId")}>
                      <option value="" data-choice>No parent (top-level category)</option>
                      {tree.items
                        .filter((c: Category) => c.id !== category.id)
                        .map((c: Category) => (
                          <option key={c.id} value={c.id}>
                            {"  ".repeat(c.depth)}
                            {c.name}
                          </option>
                        ))}
                    </SelectField>
                  </div>
                  <Button type="submit" isLoading={isSubmitting}>
                    Move
                  </Button>
                </>
              )}
            </Form>
          </FormGroup>
        </div>
      ) : null}

      {error ? <ErrorSurface error={error} action="save this category" /> : null}

      <Form<CategoryEditValues>
        validationSchema={categoryEditSchema}
        useFormProps={{ defaultValues: { name: category.name, slug: category.slug, sortOrder: String(category.sortOrder), isActive: category.isActive } }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          // Set once the update has saved a new slug: from then on the old slug's URL reads nothing.
          let savedSlug: string | undefined;
          // A slug change makes the new address the record's only one (there is no slug history), so
          // the page moves there, replacing the old entry rather than leaving it in history.
          const moveTo = (slug: string) => navigate(categoryPath(slug), { replace: true });
          try {
            // Includes the current imageHash so a name/sort-order edit here doesn't clobber an
            // image the upload block below already persisted separately.
            const slug = changedSlug(values.slug, category.slug);
            await updateCategory(category.id, { name: values.name, sortOrder: Number(values.sortOrder), imageHash, slug });
            savedSlug = slug;
            // The status is its own call (deactivate never deletes — FR-007), made only when the box changed.
            if (values.isActive !== category.isActive) await (values.isActive ? reactivateCategory : deactivateCategory)(category.id);
            toast.success("Category saved");
            if (slug) moveTo(slug);
            else goBack();
          } catch (err) {
            setError(err);
            toast.error("Could not save the category");
            // The update went through and only the status call failed: show that on the new address.
            if (savedSlug) moveTo(savedSlug);
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="The code is fixed at creation; the name, URL slug and sort order can change.">
                <Field label="Name" required maxLength={200} disabled={!canWrite} error={errors.name?.message} {...register("name")} />
                <Field
                  label="Code"
                  name="code"
                  defaultValue={category.code}
                  readOnly
                  disabled
                  className="font-mono"
                  hint={
                    <span className="flex items-center gap-1.5">
                      <Lock aria-hidden className="size-3" />
                      Permanent.
                    </span>
                  }
                />
                <Field
                  label="URL slug"
                  maxLength={200}
                  required
                  className="font-mono"
                  disabled={!canWrite}
                  hint={SLUG_HINT}
                  error={errors.slug?.message}
                  {...register("slug")}
                />
                <Field
                  label="Sort order"
                  required
                  inputMode="numeric"
                  disabled={!canWrite}
                 
                  hint="Lower sorts first among siblings."
                  error={errors.sortOrder?.message}
                  {...register("sortOrder")}
                />
              </FormGroup>

              {canWrite ? (
                <FormGroup title={<SectionTitle icon={ImageIcon}>Image</SectionTitle>} description="Optional. Uploaded as a real file, not linked.">
                  <div className="col-span-full flex flex-wrap items-center gap-4">
                    <Upload
                      accept="img"
                      multiple={false}
                      disabled={imageBusy}
                      onChange={image.onChange}
                      aria-label="Category image file"
                      placeholderText={imageBusy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                      iconClassName="size-8 shrink-0"
                      className="p-4"
                      wrapperClassName="w-full max-w-xs"
                    />
                    {imageHash ? (
                      <div className="flex flex-col items-start gap-1.5">
                        <div className="relative">
                          <img src={imageUrl(imageHash)} alt={`${category.name} — image`} className="h-20 w-20 rounded-lg border border-muted object-cover" />
                          <ActionIcon
                            type="button"
                            size="sm"
                            variant="solid"
                            color="danger"
                            className="absolute -right-2 -top-2"
                            disabled={imageBusy}
                            aria-label="Remove this image"
                            onClick={handleRemoveImage}
                          >
                            <RemoveIcon aria-hidden className="size-3.5" />
                          </ActionIcon>
                        </div>
                        {uploadedImage ? (
                          <Text className="text-xs text-gray-500">
                            {uploadedImage.sizeBytes.toLocaleString()} bytes · {uploadedImage.contentType}
                          </Text>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {imageError ?? image.error ? <ErrorSurface error={imageError ?? image.error} action="upload this image" /> : null}
                </FormGroup>
              ) : null}

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether new products can be filed under this category.">
                <StatusSwitch
                  className="@xl:col-span-2"
                  disabled={!canWrite}
                  activeText="Offered on new products."
                  inactiveText="Not offered on new products. Nothing is deleted — existing products, prices and past orders keep referring to it."
                  {...register("isActive")}
                />
              </FormGroup>
            </div>

            {canWrite ? (
              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            ) : null}
          </>
        )}
      </Form>
    </div>
  );
}

